/**
 * Date preset utilities for Sales History filters
 */

/** Timezone for OTS (On Time Shipping) dates so presets match backend (America/New_York). */
const OTS_TIMEZONE = 'America/New_York'

export type DatePreset = 'last_7_days' | 'last_3_weeks' | 'last_30_days' | 'last_90_days' | 'ytd' | 'last_12_months'

export interface DateRange {
  from: string // YYYY-MM-DD
  to: string // YYYY-MM-DD
}

/**
 * Calculate date range for a preset
 * Ensures all dates are valid (never creates invalid dates like February 30th)
 */
export function getDatePresetRange(preset: DatePreset): DateRange {
  const today = new Date()
  today.setHours(23, 59, 59, 999) // End of today
  
  // Validate today is valid
  if (!isValidDate(today)) {
    const now = new Date()
    now.setHours(23, 59, 59, 999)
    return {
      from: formatDateForAPI(getFirstDayOfMonthsAgo(2, now)),
      to: formatDateForAPI(now)
    }
  }
  
  const to = formatDateForAPI(today)
  
  let from: Date
  
  switch (preset) {
    case 'last_7_days':
      from = new Date(today)
      from.setDate(from.getDate() - 7)
      from.setHours(0, 0, 0, 0)
      break

    case 'last_3_weeks':
      from = new Date(today)
      from.setDate(from.getDate() - 21)
      from.setHours(0, 0, 0, 0)
      break
      
    case 'last_30_days':
      from = new Date(today)
      from.setDate(from.getDate() - 30)
      from.setHours(0, 0, 0, 0)
      break
      
    case 'last_90_days':
      from = new Date(today)
      from.setDate(from.getDate() - 90)
      from.setHours(0, 0, 0, 0)
      break
      
    case 'ytd':
      // January 1st of current year (always valid)
      from = new Date(today.getFullYear(), 0, 1)
      from.setHours(0, 0, 0, 0)
      break
      
    case 'last_12_months':
      from = new Date(today)
      from.setDate(from.getDate() - 365)
      from.setHours(0, 0, 0, 0)
      break
      
    default:
      from = new Date(today)
      from.setDate(from.getDate() - 30)
      from.setHours(0, 0, 0, 0)
  }
  
  // Validate the calculated date
  if (!isValidDate(from)) {
    // Fallback: use first day of 2 months ago if calculation failed
    from = getFirstDayOfMonthsAgo(2, today)
  }
  
  return {
    from: formatDateForAPI(from),
    to
  }
}

/**
 * Validate that a date is valid (not invalid like February 30th)
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime())
}

/**
 * Safely get the first day of N months ago
 * Ensures we never create invalid dates
 */
function getFirstDayOfMonthsAgo(monthsAgo: number, referenceDate: Date = new Date()): Date {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth()
  
  // Calculate target month and year, handling year rollover
  let targetYear = year
  let targetMonth = month - monthsAgo
  
  // Handle negative months (goes to previous year)
  while (targetMonth < 0) {
    targetMonth += 12
    targetYear -= 1
  }
  
  // Always use day 1, which always exists in any month
  const date = new Date(targetYear, targetMonth, 1)
  date.setHours(0, 0, 0, 0)
  
  // Validate the date
  if (!isValidDate(date)) {
    // Fallback: use today if date is invalid (should never happen)
    const fallback = new Date()
    fallback.setHours(0, 0, 0, 0)
    return fallback
  }
  
  return date
}

/**
 * Format date as YYYY-MM-DD for API
 * Uses local date components to avoid timezone shifts
 * Validates date before formatting
 */
function formatDateForAPI(date: Date): string {
  // Validate date first
  if (!isValidDate(date)) {
    // Fallback to today if invalid
    date = new Date()
  }
  
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  // Double-check that the formatted date components are valid
  // This catches edge cases where date might have been auto-corrected
  const reconstructed = new Date(year, parseInt(month) - 1, parseInt(day))
  if (!isValidDate(reconstructed) || 
      reconstructed.getFullYear() !== year ||
      reconstructed.getMonth() !== parseInt(month) - 1 ||
      reconstructed.getDate() !== parseInt(day)) {
    // If reconstruction doesn't match, use today as fallback
    const today = new Date()
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  }
  
  return `${year}-${month}-${day}`
}

/**
 * Today's date in local time as Y-m-d (for comparing with API date strings).
 */
export function getTodayLocalDateString(): string {
  return formatDateForAPI(new Date())
}

/**
 * Today's date (YYYY-MM-DD) in OTS timezone (America/New_York).
 * Exported for OTS page snapshot comparison.
 */
export function getTodayOtsDateString(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: OTS_TIMEZONE })
}

function getTodayInOtsTimezone(): string {
  return getTodayOtsDateString()
}

/**
 * Yesterday's date (YYYY-MM-DD) in OTS timezone (America/New_York).
 */
function getYesterdayInOtsTimezone(): string {
  const todayStr = getTodayInOtsTimezone()
  const [y, m, d] = todayStr.split('-').map(Number)
  const yesterday = new Date(y, m - 1, d - 1)
  return formatDateForAPI(yesterday)
}

/**
 * Today as a Date whose calendar day is "today" in OTS timezone (for preset math).
 */
function getTodayDateInOtsTimezone(): Date {
  const todayStr = getTodayInOtsTimezone()
  const [y, m, day] = todayStr.split('-').map(Number)
  const toDate = new Date(y, m - 1, day)
  toDate.setHours(23, 59, 59, 999)
  return toDate
}

/**
 * Get yesterday as a single-day date range (from and to both yesterday).
 * Used for OTS (On Time Shipping) report default, matching the top bar and backend (America/New_York).
 */
export function getYesterdayDateRange(): DateRange {
  const dateStr = getYesterdayInOtsTimezone()
  return { from: dateStr, to: dateStr }
}

/**
 * Get default date range for Sales History filters
 * Returns the previous 3 weeks (21 days) through today (local calendar dates).
 */
export function getDefaultDateRange(): DateRange {
  return getDatePresetRange('last_3_weeks')
}

/**
 * Get preset label
 */
export function getPresetLabel(preset: DatePreset): string {
  const labels: Record<DatePreset, string> = {
    last_7_days: 'Last 7 Days',
    last_3_weeks: 'Previous 3 Weeks',
    last_30_days: 'Last 30 Days',
    last_90_days: 'Last 90 Days',
    ytd: 'Year to Date',
    last_12_months: 'Last 12 Months'
  }
  return labels[preset]
}

/**
 * Date preset options for dropdown
 */
export const datePresetOptions = [
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_3_weeks', label: 'Previous 3 Weeks' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last_12_months', label: 'Last 12 Months' }
] as const

// --- OTS (On Time Shipping) date presets (Monday = start of week) ---

export type OtsDatePreset =
  | 'current_week'
  | 'last_week'
  | 'last_two_weeks'
  | 'current_month'
  | 'previous_month'
  | 'this_quarter'
  | 'previous_quarter'
  | 'this_year'
  | 'last_year'

/** Monday of the week containing d (ISO week: Monday = start) */
function getMondayOf(d: Date): Date {
  const day = d.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
  const daysSinceMonday = (day - 1 + 7) % 7
  const monday = new Date(d)
  monday.setDate(d.getDate() - daysSinceMonday)
  monday.setHours(0, 0, 0, 0)
  return monday
}

/**
 * OTS date preset range (YYYY-MM-DD). Monday = start of week.
 * Uses America/New_York for "today" so presets match backend and top bar.
 * Does not auto-fetch; user still clicks Apply.
 */
export function getOtsDatePresetRange(preset: OtsDatePreset): DateRange {
  const toDate = getTodayDateInOtsTimezone()
  const to = formatDateForAPI(toDate)

  let from: Date
  let toEnd: Date = new Date(toDate)
  let endBeforeToday = false

  switch (preset) {
    case 'current_week': {
      from = getMondayOf(toDate)
      break
    }
    case 'last_week': {
      const lastWeekMonday = getMondayOf(toDate)
      lastWeekMonday.setDate(lastWeekMonday.getDate() - 7)
      from = lastWeekMonday
      const lastWeekSunday = new Date(from)
      lastWeekSunday.setDate(from.getDate() + 6)
      lastWeekSunday.setHours(23, 59, 59, 999)
      toEnd = lastWeekSunday
      break
    }
    case 'current_month': {
      from = new Date(toDate.getFullYear(), toDate.getMonth(), 1)
      from.setHours(0, 0, 0, 0)
      break
    }
    case 'previous_month': {
      from = new Date(toDate.getFullYear(), toDate.getMonth() - 1, 1)
      from.setHours(0, 0, 0, 0)
      const lastDayPrev = new Date(toDate.getFullYear(), toDate.getMonth(), 0)
      lastDayPrev.setHours(23, 59, 59, 999)
      toEnd = lastDayPrev
      break
    }
    case 'this_quarter': {
      const q = Math.floor(toDate.getMonth() / 3) + 1
      from = new Date(toDate.getFullYear(), (q - 1) * 3, 1)
      from.setHours(0, 0, 0, 0)
      break
    }
    case 'previous_quarter': {
      const q = Math.floor(toDate.getMonth() / 3) + 1
      const prevQ = q === 1 ? 4 : q - 1
      const prevYear = q === 1 ? toDate.getFullYear() - 1 : toDate.getFullYear()
      from = new Date(prevYear, (prevQ - 1) * 3, 1)
      from.setHours(0, 0, 0, 0)
      const lastDayPrevQ = new Date(prevYear, (prevQ - 1) * 3 + 3, 0)
      lastDayPrevQ.setHours(23, 59, 59, 999)
      toEnd = lastDayPrevQ
      break
    }
    case 'this_year': {
      from = new Date(toDate.getFullYear(), 0, 1)
      from.setHours(0, 0, 0, 0)
      break
    }
    case 'last_year': {
      from = new Date(toDate.getFullYear() - 1, 0, 1)
      from.setHours(0, 0, 0, 0)
      const lastDayPrevYear = new Date(toDate.getFullYear() - 1, 11, 31)
      lastDayPrevYear.setHours(23, 59, 59, 999)
      toEnd = lastDayPrevYear
      break
    }
    // 14 completed days ending yesterday (today excluded) — matches the backend OTS KPI window.
    // default shares this so an unknown preset falls back to the same trailing window.
    case 'last_two_weeks':
    default: {
      from = new Date(toDate)
      from.setDate(from.getDate() - 14)
      from.setHours(0, 0, 0, 0)
      toEnd = new Date(toDate)
      toEnd.setDate(toEnd.getDate() - 1)
      toEnd.setHours(23, 59, 59, 999)
      endBeforeToday = true
    }
  }

  if (!isValidDate(from)) {
    from = getFirstDayOfMonthsAgo(1, toDate)
  }
  const fromStr = formatDateForAPI(from)
  const toStr = endBeforeToday || preset === 'last_week' || preset === 'previous_month' || preset === 'previous_quarter' || preset === 'last_year'
    ? formatDateForAPI(toEnd)
    : to
  return { from: fromStr, to: toStr }
}

/**
 * Default date range for the On Time Shipping page when the URL has no date query params.
 * Trailing 14 completed days (today−14 through yesterday, America/New_York) to match the nav KPI window.
 */
export function getOtsDefaultPageDateRange(): DateRange {
  return getOtsDatePresetRange('last_two_weeks')
}

export const otsDatePresetOptions: { value: OtsDatePreset; label: string }[] = [
  { value: 'current_week', label: 'Current week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'last_two_weeks', label: 'Last two weeks' },
  { value: 'current_month', label: 'Current month' },
  { value: 'previous_month', label: 'Previous month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'previous_quarter', label: 'Previous quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
]

/**
 * True when the selected YYYY-MM-DD range exactly matches the canonical range for a preset
 * (same rules as {@link getOtsDatePresetRange} at evaluation time).
 */
function otsRangeMatchesSelection(range: DateRange, from: string, to: string): boolean {
  return range.from === from && range.to === to
}

/**
 * Returns which OTS date preset matches the current from/to, or null if none match.
 * Compares strictly to {@link getOtsDatePresetRange} for each preset in {@link otsDatePresetOptions} order.
 */
export function findActiveOtsDatePreset(
  from: string | null | undefined,
  to: string | null | undefined,
): OtsDatePreset | null {
  if (from == null || to == null) return null
  const fs = String(from).trim()
  const ts = String(to).trim()
  if (fs === '' || ts === '') return null
  for (const opt of otsDatePresetOptions) {
    const range = getOtsDatePresetRange(opt.value)
    if (otsRangeMatchesSelection(range, fs, ts)) return opt.value
  }
  return null
}

